import React, { useState } from 'react';
import { Menu, Building2, FolderKanban, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTenant } from '@/contexts/TenantContext';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';

export const MobileMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [showWorkspaces, setShowWorkspaces] = useState(false);
  const [showTenants, setShowTenants] = useState(false);
  
  const { workspaces, currentWorkspace, switchWorkspace } = useWorkspace();
  const { tenants, currentTenant, switchTenant } = useTenant();
  const navigate = useNavigate();

  const activeWorkspaces = workspaces.filter(ws => ws.status !== 'ARCHIVED');
  const isOwnerOrAdmin = currentTenant?.role === 'OWNER' || currentTenant?.role === 'ADMIN';

  const handleWorkspaceSwitch = (workspaceId: string) => {
    switchWorkspace(workspaceId);
    setShowWorkspaces(false);
  };

  const handleTenantSwitch = async (tenantId: string) => {
    await switchTenant(tenantId);
    setShowTenants(false);
  };

  const handleCreateWorkspace = () => {
    setOpen(false);
    navigate('/settings?section=workspace');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="sm:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[300px] sm:hidden p-0">
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle className="text-left">Menu</SheetTitle>
        </SheetHeader>
        
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Current Workspace Section */}
          <div className="px-6 pb-3">
            <button
              onClick={() => setShowWorkspaces(!showWorkspaces)}
              className="w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <FolderKanban 
                  className="h-5 w-5 shrink-0" 
                  style={{ color: currentWorkspace?.color || 'currentColor' }}
                />
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-xs text-muted-foreground">Workspace</span>
                  <span className="text-sm font-medium truncate max-w-[160px]">
                    {currentWorkspace?.name || 'Select workspace'}
                  </span>
                </div>
              </div>
              <ChevronRight className={`h-4 w-4 transition-transform ${showWorkspaces ? 'rotate-90' : ''}`} />
            </button>
            
            {/* Workspace List */}
            {showWorkspaces && (
              <div className="mt-2 space-y-1 pl-2">
                {activeWorkspaces.map((workspace) => (
                  <button
                    key={workspace.workspaceId}
                    onClick={() => handleWorkspaceSwitch(workspace.workspaceId)}
                    className="w-full flex items-center justify-between py-2 px-3 rounded-md hover:bg-accent transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderKanban 
                        className="h-4 w-4 shrink-0" 
                        style={{ color: workspace.color || 'currentColor' }}
                      />
                      <span className="text-sm truncate">{workspace.name}</span>
                      {workspace.isDefault && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          Default
                        </Badge>
                      )}
                    </div>
                    {currentWorkspace?.workspaceId === workspace.workspaceId && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </button>
                ))}
                {isOwnerOrAdmin && (
                  <button
                    onClick={handleCreateWorkspace}
                    className="w-full flex items-center gap-2 py-2 px-3 rounded-md hover:bg-accent transition-colors text-left text-sm text-muted-foreground"
                  >
                    <span>+ Create Workspace</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Current Tenant Section */}
          {tenants.length > 1 && (
            <div className="px-6 py-3">
              <button
                onClick={() => setShowTenants(!showTenants)}
                className="w-full flex items-center justify-between py-3 px-3 rounded-lg hover:bg-accent transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Building2 className="h-5 w-5 shrink-0" />
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-xs text-muted-foreground">Organization</span>
                    <span className="text-sm font-medium truncate max-w-[160px]">
                      {currentTenant?.tenantName || 'Select organization'}
                    </span>
                  </div>
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${showTenants ? 'rotate-90' : ''}`} />
              </button>
              
              {/* Tenant List */}
              {showTenants && (
                <div className="mt-2 space-y-1 pl-2">
                  {tenants.map((tenant) => (
                    <button
                      key={tenant.tenantId}
                      onClick={() => handleTenantSwitch(tenant.tenantId)}
                      className="w-full flex items-center justify-between py-2 px-3 rounded-md hover:bg-accent transition-colors text-left"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="h-4 w-4 shrink-0" />
                        <span className="text-sm truncate">{tenant.tenantName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={tenant.role === 'OWNER' ? 'default' : 'outline'} className="text-[10px] px-1.5 py-0">
                          {tenant.role}
                        </Badge>
                        {currentTenant?.tenantId === tenant.tenantId && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
